import { Col, type ColProps } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import PasswordField from '../../../../components/fields/PasswordField';
import SingleSecretUploadField from '../../../../components/fields/SingleSecretUploadField';

const defaultColSpan = { lg: 8, md: 12, xs: 24 };

export type EazyInsurerIntegrationProps = {
    prefix?: string;
    colSpan?: ColProps;
    disabled?: boolean;
};

const fieldNames = [
    'username',
    'password',
    'apiUrl',
    'sftpHost',
    'sftpUser',
    'sftpPort',
    'privateKey',
    'certCA',
] as const;

const EazyInsurerIntegration = ({
    prefix,
    colSpan = defaultColSpan,
    disabled = false,
}: EazyInsurerIntegrationProps) => {
    const { t } = useTranslation(['insurerDetails']);

    const names = useMemo(
        () =>
            Object.fromEntries(fieldNames.map(name => [name, prefix ? `${prefix}.${name}` : name])) as Record<
                (typeof fieldNames)[number],
                string
            >,
        [prefix]
    );

    return (
        <>
            <Col {...colSpan}>
                <InputField
                    {...t('insurerDetails:fields.eazyInsurerIntegration.username', { returnObjects: true })}
                    disabled={disabled}
                    name={names.username}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <PasswordField
                    {...t('insurerDetails:fields.eazyInsurerIntegration.password', { returnObjects: true })}
                    disabled={disabled}
                    name={names.password}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('insurerDetails:fields.eazyInsurerIntegration.apiUrl', { returnObjects: true })}
                    disabled={disabled}
                    name={names.apiUrl}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('insurerDetails:fields.eazyInsurerIntegration.sftpHost', { returnObjects: true })}
                    disabled={disabled}
                    name={names.sftpHost}
                />
            </Col>
            <Col {...colSpan}>
                <InputNumberField
                    {...t('insurerDetails:fields.eazyInsurerIntegration.sftpPort', { returnObjects: true })}
                    disabled={disabled}
                    name={names.sftpPort}
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('insurerDetails:fields.eazyInsurerIntegration.sftpUser', { returnObjects: true })}
                    disabled={disabled}
                    name={names.sftpUser}
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('insurerDetails:fields.eazyInsurerIntegration.privateKey', { returnObjects: true })}
                    disabled={disabled}
                    name={names.privateKey}
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('insurerDetails:fields.eazyInsurerIntegration.certCA', { returnObjects: true })}
                    disabled={disabled}
                    name={names.certCA}
                />
            </Col>
        </>
    );
};

export default EazyInsurerIntegration;
